/**
 * Admin Management Types
 * Requirements: 1.1, 1.2, 3.1, 3.2
 */

// User role enum matching backend
export enum UserRole {
  PLATFORM_ADMIN = 'PLATFORM_ADMIN',
  ADMIN = 'ADMIN',
  USER = 'USER'
}

// User status enum
export enum UserStatus {
  ACTIVE = 'ACTIVE',
  INACTIVE = 'INACTIVE',
  PENDING_SYNC = 'PENDING_SYNC'
}

// Tenant status enum
export enum TenantStatus {
  ACTIVE = 'ACTIVE',
  INACTIVE = 'INACTIVE'
}

// Tenant feature enum
export enum TenantFeature {
  DUE_DILIGENCE_ANALYSIS = 'due_diligence_analysis',
  BORROWER_ANALYSIS = 'borrower_analysis'
}

// User entity
export interface User {
  userId: string;
  tenantId: string | null;
  role: UserRole;
  cognitoSub: string | null;
  email: string;
  name: string;
  status: UserStatus;
  createdAt: string;
  updatedAt: string;
}

// Tenant entity
export interface Tenant {
  tenantId: string;
  name: string;
  status: TenantStatus;
  userCount: number;
  enabledFeatures?: string[]; // Make it optional with default
  createdAt: string;
  updatedAt: string;
}

// Input types for mutations
export interface CreateTenantInput {
  name: string;
}

export interface UpdateTenantInput {
  name?: string;
  status?: TenantStatus;
  enabledFeatures?: string[];
}

export interface UpdateTenantFeaturesInput {
  enabledFeatures: string[];
}

export interface CreateUserInput {
  email: string;
  name: string;
  role?: UserRole;
}

export interface UpdateUserInput {
  email?: string;
  name?: string;
  status?: UserStatus;
}

export interface AssignRoleInput {
  userId: string;
  role: UserRole;
}

// Connection types for pagination
export interface TenantConnection {
  items: Tenant[];
  nextToken: string | null;
}

export interface UserConnection {
  items: User[];
  nextToken: string | null;
}

// Current user context for auth
export interface CurrentUser {
  userId: string;
  tenantId: string | null;
  role: UserRole;
  email: string;
  name: string;
}

// Feature access check response
export interface FeatureAccessResponse {
  tenantId: string;
  feature: string;
  hasAccess: boolean;
}

// Tenant features response
export interface TenantFeaturesResponse {
  tenantId: string;
  enabledFeatures: string[];
}

// ============================================
// Files manager (platform admin)
// ============================================

export interface FilesManagerModel {
  id: string;
  label: string;
  provider: string;
  supportsVision: boolean;
}

export interface FilesManagerSettings {
  visionModelId: string;
  extractionModelId: string;
  availableModels: FilesManagerModel[];
  updatedAt: string | null;
  updatedBy: string | null;
}

export interface UpdateFilesManagerSettingsInput {
  visionModelId: string;
  extractionModelId: string;
}

export interface FilesManagerStageUsage {
  stage: string;
  modelId: string;
  inputTokens: number;
  outputTokens: number;
  requests: number;
}

export type FilesManagerRunStatus = 'RUNNING' | 'SUCCESS' | 'FAILED';

export interface FilesManagerRun {
  invocationId: string;
  tenantId: string;
  jobId: string | null;
  loanId: string | null;
  fileId: string | null;
  fileName: string | null;
  status: FilesManagerRunStatus | string;
  visionModelId: string;
  extractionModelId: string;
  startedAt: string;
  finishedAt: string | null;
  durationMs: number | null;
  pageCount: number | null;
  documentCount: number | null;
  inputTokens: number;
  outputTokens: number;
  totalTokens: number;
  stages: FilesManagerStageUsage[];
  errorMessage: string | null;
}

export interface FilesManagerRunConnection {
  items: FilesManagerRun[];
  nextToken: string | null;
}
